import { useEffect, useState } from "react";
import { useNavigate, useLocation, useMatch } from "react-router-dom";
import { api } from "@/lib/api";
import { Building2, ChevronDown } from "lucide-react";
import { useLang } from "@/lib/i18n";

/**
 * ClubSwitcher — dropdown in de header waarmee de gebruiker snel tussen
 * eigen clubs kan wisselen zonder eerst terug naar het dashboard te moeten.
 *
 * • Verborgen wanneer user ≤ 1 club heeft (dan is er niks te kiezen).
 * • Actieve club (uit `/clubs/{id}/...`) staat als label bovenaan.
 * • Sluit vanzelf op scroll of outside-click.
 */
export default function ClubSwitcher() {
  const { t } = useLang();
  const nav = useNavigate();
  const location = useLocation();
  const clubMatch = useMatch("/clubs/:cid/*");
  const currentCid = clubMatch?.params?.cid || null;

  const [clubs, setClubs] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch clubs on mount + whenever we land on a new route (e.g. after
  // creating/deleting a club) — keeps the list fresh.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/clubs");
        if (!cancelled) setClubs(r.data || []);
      } catch {
        // Not authenticated / trial expired — hide switcher silently.
        if (!cancelled) setClubs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close menu on outside click (simple document listener).
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!e.target.closest?.("[data-club-switcher]")) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Nothing useful to show — hide completely.
  if (loading || clubs.length <= 1) return null;

  const active = clubs.find((c) => c.id === currentCid);
  const label = active?.name || t("Kies club");

  return (
    <div className="relative" data-club-switcher data-testid="club-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="club-switcher-btn"
        className="h-10 px-3 flex items-center gap-2 font-display uppercase font-bold text-sm bg-background text-foreground border-2 border-foreground hover:bg-muted max-w-[220px]"
      >
        <Building2 className="w-4 h-4 flex-shrink-0" />
        <span className="truncate">{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1 min-w-[260px] max-h-[70vh] overflow-y-auto bg-card border-2 border-foreground heavy-shadow z-50"
          data-testid="club-switcher-menu"
        >
          {clubs.map((c) => {
            const isActive = c.id === currentCid;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => { setOpen(false); nav(`/clubs/${c.id}`); }}
                data-testid={`club-switcher-item-${c.id}`}
                className={`w-full text-left px-4 py-2.5 flex items-center gap-2 text-sm border-b border-border last:border-b-0 hover:bg-muted ${isActive ? "bg-foreground text-background font-bold" : ""}`}
              >
                <Building2 className="w-4 h-4 flex-shrink-0 opacity-60" />
                <span className="truncate">{c.name}</span>
                {c.is_demo && <span className="ml-auto text-[10px] tracking-widest opacity-70">DEMO</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
